/**
 * Verity is replying.
 *
 * A turn costs two or three model round-trips — get_requirements, a policy or
 * provider lookup, then send_message — which is fifteen to twenty-five seconds
 * of nothing on screen. Nothing on screen reads as broken, and a borrower who
 * types again into a locked field learns that the app ignores them.
 *
 * What the market settled on, and what the research behind it says: the three
 * dots in the speaker's own bubble are universally read as "someone is
 * replying" and shorten the *felt* wait; but past a couple of seconds a bare
 * "typing…" starts to read as slow, and a status line that names what is
 * happening does better than dots alone. So the dots appear the instant the
 * message leaves, and after a beat a line beneath them says what Verity is
 * doing — in her voice, and only things that are true of every turn.
 *
 * The stages are timed, not streamed. The server answers one POST with the
 * whole turn, so there is no tool-call event to show; the words are chosen to
 * be honest at any point in the loop rather than to claim a step that may not
 * be running. Streaming the loop's steps would make this exact, and is the
 * upgrade path (see next-steps).
 *
 * Announced once to assistive tech through a polite live region, not on every
 * stage change — a screen reader user wants "Verity is replying", not a
 * narrated progress bar.
 */

import { useEffect, useRef, useState } from "react";

import { VerityMark } from "./Header";

/** What the line beneath the dots says, by seconds waited. */
export const STAGES: { after: number; text: string }[] = [
  { after: 0, text: "" },
  { after: 2.5, text: "Checking your loan file" },
  { after: 8, text: "Looking that up" },
  { after: 16, text: "Writing back to you" },
  { after: 30, text: "Still here — this one is taking a moment" },
];

export function stageFor(seconds: number): string {
  let text = "";
  for (const stage of STAGES) if (seconds >= stage.after) text = stage.text;
  return text;
}

export function TypingIndicator({ showLabel = true }: { showLabel?: boolean }) {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(() => setSeconds((Date.now() - started) / 1000), 500);
    return () => window.clearInterval(timer);
  }, []);

  const status = stageFor(seconds);

  // The bubble grows when the status line appears, and the thread has already
  // scrolled to where its bottom *was*. Keep the whole indicator in view.
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [status]);

  return (
    <div ref={ref} className="flex flex-col gap-1.5" data-testid="typing-indicator">
      {showLabel && (
        <p className="font-mono text-[11px] tracking-widest text-verity-600 uppercase">Verity</p>
      )}
      <div className="flex items-end gap-2">
        <span className="w-8 shrink-0">
          <span
            className="grid size-8 place-items-center rounded-md"
            style={{ background: "var(--brand-grad)" }}
            aria-hidden
          >
            <VerityMark className="size-4 [&_path]:fill-white [&_circle]:fill-indigo-450" />
          </span>
        </span>

        <div
          role="status"
          aria-live="polite"
          className="rounded-xl bg-bubble-agent px-4 py-3"
        >
          {/* The one sentence a screen reader hears. */}
          <span className="sr-only">Verity is replying</span>

          <span className="flex h-5 items-center gap-1" aria-hidden>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="verity-dot size-2 rounded-full bg-verity-500"
                style={{ animationDelay: `${i * 160}ms` }}
              />
            ))}
          </span>

          {status && (
            <p aria-hidden className="mt-1 text-[13px] text-ink-500">
              {status}…
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
